/** @file Builds lookup indexes over a document: name→id per kind, bone children, reverse references. */
import type { Doc } from './schema';
import { ENTITY_KINDS, type EntityKind } from './entityKinds';
import type { Id } from '../ids';

export interface DocIndex {
  /** name → id, per entity kind. */
  byName: ReadonlyMap<EntityKind, ReadonlyMap<string, Id>>;
  /** kind of every id in the document. */
  kindOf: ReadonlyMap<Id, EntityKind>;
  /** bone id → child bone ids, sorted by siblingIndex. */
  children: ReadonlyMap<Id, readonly Id[]>;
  /** id → ids of entities that reference it (slots→bones, attachments→images, skins→attachments, ...). */
  referencedBy: ReadonlyMap<Id, ReadonlySet<Id>>;
}

const addRef = (map: Map<Id, Set<Id>>, target: Id, from: Id): void => {
  let set = map.get(target);
  if (!set) { set = new Set(); map.set(target, set); }
  set.add(from);
};

function collectSkeletonReferences(doc: Doc, refs: Map<Id, Set<Id>>): void {
  for (const [id, b] of Object.entries(doc.bones)) if (b.parent) addRef(refs, b.parent, id);
  for (const [id, s] of Object.entries(doc.slots)) addRef(refs, s.bone, id);
  for (const [id, c] of Object.entries(doc.constraints)) {
    addRef(refs, c.target, id);
    for (const b of c.bones) addRef(refs, b, id);
  }
  for (const [id, s] of Object.entries(doc.sockets)) {
    addRef(refs, s.parentBone, id);
    addRef(refs, s.bone, id);
    if (s.grip2Bone) addRef(refs, s.grip2Bone, id);
  }
  for (const [id, r] of Object.entries(doc.references)) addRef(refs, r.image, id);
}

function collectAttachmentReferences(doc: Doc, refs: Map<Id, Set<Id>>): void {
  for (const [id, a] of Object.entries(doc.attachments)) {
    addRef(refs, a.image, id);
    if (a.mount) addRef(refs, a.mount.socket, id);
    if (a.type !== 'mesh' || a.geometry.kind !== 'skinned') continue;
    for (const vertex of a.geometry.weights) for (const [bone] of vertex) addRef(refs, bone, id);
  }
  for (const [id, sk] of Object.entries(doc.skins)) {
    for (const e of sk.entries) {
      addRef(refs, e.slot, id);
      addRef(refs, e.attachment, id);
    }
  }
}

function collectAnimationReferences(doc: Doc, refs: Map<Id, Set<Id>>): void {
  for (const [id, an] of Object.entries(doc.animations)) {
    const targets = [...Object.keys(an.bones), ...Object.keys(an.slots), ...Object.keys(an.ik), ...Object.keys(an.deform), ...an.events.map((e) => e.event)];
    for (const t of targets) addRef(refs, t, id);
  }
}

function collectReferences(doc: Doc, refs: Map<Id, Set<Id>>): void {
  collectSkeletonReferences(doc, refs);
  collectAttachmentReferences(doc, refs);
  collectAnimationReferences(doc, refs);
}

/** Builds all indexes in one pass; O(size of document). */
export function docIndex(doc: Doc): DocIndex {
  const byName = new Map<EntityKind, Map<string, Id>>();
  const kindOf = new Map<Id, EntityKind>();
  for (const kind of Object.keys(ENTITY_KINDS) as EntityKind[]) {
    const table = doc[ENTITY_KINDS[kind]] as Record<Id, { name: string }>;
    const m = new Map<string, Id>();
    for (const [id, entity] of Object.entries(table)) { m.set(entity.name, id); kindOf.set(id, kind); }
    byName.set(kind, m);
  }
  const childLists = new Map<Id, Id[]>();
  for (const [id, b] of Object.entries(doc.bones)) {
    if (!b.parent) continue;
    const list = childLists.get(b.parent) ?? [];
    list.push(id);
    childLists.set(b.parent, list);
  }
  for (const list of childLists.values()) list.sort((a, b) => (doc.bones[a]?.siblingIndex ?? 0) - (doc.bones[b]?.siblingIndex ?? 0));
  const referencedBy = new Map<Id, Set<Id>>();
  collectReferences(doc, referencedBy);
  return { byName, kindOf, children: childLists, referencedBy };
}
